// cspell:ignore hitbox hitboxes
import type { GlyphInfo, GlyphRenderInstruction, OrientationValue, WritingModeValue } from '@meitu/x-design-text';
import type { TextRenderSnapshot as Snapshot } from '@/framework/services/text-render';
import type { IPoint } from '@/version-types';

export type HitBox = {
    x: number;
    y: number;
    width: number;
    height: number;
    byteStart: number;
    byteEnd: number;
    clusterText: string;
    lineIndex: number;
    bidiLevel: number;
    writingMode: WritingModeValue;
};

/**
 * 按不可变 snapshot 身份缓存轻量索引和已经实际请求过的行 hitboxes。
 *
 * WeakMap 不延长 snapshot 生命周期；同一 snapshot 的点击、caret 和选区计算可以共享分桶结果，
 * 新 snapshot 则天然获得隔离索引，不需要主动清理。
 */
const hitboxCache = new WeakMap<Snapshot, HitboxIndex>();

/**
 * 计算数值到闭区间的最短距离。
 *
 * @param value - 待测数值。
 * @param start - 区间起点。
 * @param end - 区间终点。
 * @returns 数值位于区间内时返回 0，否则返回到最近端点的距离。
 */
const distanceToInterval = (value: number, start: number, end: number) => {
    if (value < start) return start - value;
    if (value > end) return value - end;
    return 0;
};

/**
 * 查找坐标点在块方向上距离最近的排版行。
 *
 * @param snapshot - 当前排版快照。
 * @param point - 画布坐标点。
 * @returns 最近的行索引；没有排版行时返回 -1。
 */
export const findLineIndexByPoint = (snapshot: Snapshot, point: IPoint) => {
    const vertical = snapshot.writingMode === 'vertical-rl';
    let bestIndex = -1;
    let bestDistance = Infinity;

    snapshot.lineMetrics.forEach((line, index) => {
        const distance = vertical
            ? distanceToInterval(point.x, line.leftEdgeX, line.rightEdgeX)
            : distanceToInterval(point.y, line.top, line.bottom);
        if (distance < bestDistance) {
            bestDistance = distance;
            bestIndex = index;
        }
    });

    return bestIndex;
};

/**
 * 读取命中盒在当前书写方向上的起点。
 *
 * @param hitbox - 目标命中盒。
 * @returns 横排时返回 x，竖排时返回 y。
 */
const getInlineStart = (hitbox: HitBox) => (hitbox.writingMode === 'vertical-rl' ? hitbox.y : hitbox.x);

/**
 * 读取命中盒在当前书写方向上的尺寸。
 *
 * @param hitbox - 目标命中盒。
 * @returns 横排时返回 width，竖排时返回 height。
 */
const getInlineSize = (hitbox: HitBox) => (hitbox.writingMode === 'vertical-rl' ? hitbox.height : hitbox.width);

/**
 * 将相同行、方向和连续视觉区间的命中盒分组。
 *
 * @param hitboxes - 待分组的命中盒。
 * @returns 按行和视觉起点排序的连续视觉分组。
 */
export const groupByVisualRun = (hitboxes: readonly HitBox[]) => {
    const sorted = [...hitboxes].sort((a, b) => a.lineIndex - b.lineIndex || getInlineStart(a) - getInlineStart(b));
    const groups: HitBox[][] = [];

    for (const hitbox of sorted) {
        const group = groups[groups.length - 1];
        const previous = group?.[group.length - 1];
        if (
            group?.[0]?.lineIndex === hitbox.lineIndex &&
            group[0].bidiLevel === hitbox.bidiLevel &&
            previous &&
            getInlineStart(previous) + getInlineSize(previous) >= getInlineStart(hitbox)
        ) {
            group.push(hitbox);
        } else {
            groups.push([hitbox]);
        }
    }

    return groups;
};

/**
 * 竖排 GlyphInfo.y / instruction.y 是绘制原点，不是 advance 盒顶边：
 * - upright path：接近 baseline（字格中下部）
 * - rotated90Cw path：约为 advance 盒顶边
 * - delegated：y 为顶边，并自带 advance
 *
 * 横排可直接用 glyph.x 当行内起点；竖排必须先还原字格顶边，caret/选区才会对齐墨迹。
 */
type VerticalInlineEntry = {
    originY: number;
    isUpright: boolean;
    knownAdvance?: number;
};

/**
 * 建立字形键到竖排方向的映射。
 *
 * @param instructions - 文本引擎绘制指令。
 * @returns 字形键对应的方向映射。
 */
const buildOrientationByGlyphKey = (instructions: readonly GlyphRenderInstruction[]) => {
    const orientationByGlyphKey = new Map<number, OrientationValue>();
    for (const instruction of instructions) {
        if (!('glyphKey' in instruction) || !('orientation' in instruction)) continue;
        orientationByGlyphKey.set(instruction.glyphKey, instruction.orientation);
    }
    return orientationByGlyphKey;
};

/**
 * 推算 upright 字形 baseline 相对字格顶边的固定 inset。
 *
 * 优先从「行首到第一个 upright 之间、可确定长度的前缀」还原：
 * - 行首就是 upright：offset = originY - line.top
 * - 前缀只有 delegated（已知 advance）：offset = originY - (line.top + Σ advance)
 * - 前缀含未知 advance 的 rotated 时退回 originY - line.top（纯 CJK / 行首 upright 仍正确）
 *
 * @param lineTop - 排版行顶边。
 * @param entries - 按视觉顺序排列的竖排行内项。
 * @returns upright 字形 baseline 相对字格顶边的偏移。
 */
const resolveUprightBaselineOffset = (lineTop: number, entries: readonly VerticalInlineEntry[]) => {
    const firstUprightIndex = entries.findIndex((entry) => entry.isUpright);
    if (firstUprightIndex < 0) return 0;

    let prefixEnd = lineTop;
    for (let index = 0; index < firstUprightIndex; index++) {
        const entry = entries[index];
        if (entry.knownAdvance == null) {
            return entries[firstUprightIndex].originY - lineTop;
        }
        prefixEnd = entry.originY + entry.knownAdvance;
    }

    return entries[firstUprightIndex].originY - prefixEnd;
};

/**
 * 将竖排绘制原点还原为 advance 字格。
 *
 * @param lineTop - 排版行顶边。
 * @param lineBottom - 排版行底边。
 * @param listMarkerIndent - 列表缩进。
 * @param entries - 按视觉顺序排列的竖排行内项。
 * @returns 每个行内项对应的起点和尺寸。
 */
const resolveVerticalInlineBoxes = (
    lineTop: number,
    lineBottom: number,
    listMarkerIndent: number,
    entries: readonly VerticalInlineEntry[],
) => {
    if (entries.length === 0) return [] as Array<{ start: number; size: number }>;

    const baselineOffset = resolveUprightBaselineOffset(lineTop, entries);
    const starts = entries.map((entry) => {
        const start = entry.isUpright ? entry.originY - baselineOffset : entry.originY;
        return start + listMarkerIndent;
    });

    return starts.map((start, index) => {
        const end = index + 1 < starts.length ? starts[index + 1] : lineBottom + listMarkerIndent;
        return {
            start,
            size: Math.max(0, end - start),
        };
    });
};

type DelegatedInstruction = Extract<GlyphRenderInstruction, { kind: 'delegated' }>;

/**
 * 一个 snapshot 的按行命中索引。
 *
 * `glyphsByLine` 与 `delegatedByLine` 保存一次线性分桶结果；`lineOffsets` 把引擎行内 byte offset
 * 还原为全文 offset；`byLine` 只缓存已经被 caret/点击实际请求的行。`all` 仅在非折叠选区等确实
 * 需要全文 hitboxes 的路径中生成。
 */
type HitboxIndex = {
    glyphByByteStart: Map<number, GlyphInfo>;
    glyphsByLine: GlyphInfo[][];
    delegatedByLine: DelegatedInstruction[][];
    delegatedBytes: Set<number>;
    lineOffsets: number[];
    orientationByGlyphKey: Map<number, OrientationValue>;
    byLine: Array<HitBox[] | undefined>;
    all?: HitBox[];
};

/**
 * 计算坐标到指定排版行交叉轴范围的距离。
 *
 * 横排比较 Y 与行的 top/bottom，竖排比较 X 与列的 left/right；坐标落在范围内时距离为 0。
 *
 * @param snapshot - 当前排版快照。
 * @param lineIndex - 待比较的行索引。
 * @param point - glyph 或 delegated instruction 的排版坐标。
 * @returns 到该行交叉轴范围的非负距离；行不存在时返回 Infinity。
 */
const lineDistance = (snapshot: Snapshot, lineIndex: number, point: IPoint) => {
    const line = snapshot.lineMetrics[lineIndex];
    if (!line) return Infinity;
    return snapshot.writingMode === 'vertical-rl'
        ? distanceToInterval(point.x, line.leftEdgeX, line.rightEdgeX)
        : distanceToInterval(point.y, line.top, line.bottom);
};

/**
 * 利用引擎输出的行序单调性，从当前游标向后定位坐标所属的最近排版行。
 *
 * glyph infos 与 delegated instructions 都按排版顺序输出，因此下一项不会回到已经越过的行；游标只向前
 * 移动，可把逐项扫描全部行的 O(items × lines) 分桶降为 O(items + lines)。若相邻两行距离相同，
 * 继续前进以保持既有 `findLineIndexByPoint()` 选择后一个候选行的语义。
 *
 * @param snapshot - 当前排版快照。
 * @param point - 当前待分桶项的排版坐标。
 * @param cursor - 上一个项目命中的行索引。
 * @returns 当前项目所属的行索引。
 */
const advanceLineCursor = (snapshot: Snapshot, point: IPoint, cursor: number) => {
    let next = cursor;
    while (
        next + 1 < snapshot.lineMetrics.length &&
        lineDistance(snapshot, next + 1, point) <= lineDistance(snapshot, next, point)
    ) {
        next += 1;
    }
    return next;
};

/**
 * 为 snapshot 建立一次按行索引，但不创建任何 HitBox。
 *
 * 正文 glyph 与浏览器 delegated instruction 分别使用单调游标分桶；delegated 对应的普通 glyph
 * 通过 byteStart 标记后会在实际建盒时排除，防止同一字符生成重复命中区域。
 *
 * @param snapshot - 当前不可变排版快照。
 * @returns 可供各行懒构建 hitboxes 的共享索引。
 */
const createHitboxIndex = (snapshot: Snapshot): HitboxIndex => {
    const glyphByByteStart = new Map<number, GlyphInfo>();
    const glyphsByLine = snapshot.lineMetrics.map(() => [] as GlyphInfo[]);
    let lineCursor = 0;
    for (const glyph of snapshot.glyphInfos) {
        glyphByByteStart.set(glyph.start, glyph);
        lineCursor = advanceLineCursor(snapshot, { x: glyph.x, y: glyph.y }, lineCursor);
        glyphsByLine[lineCursor]?.push(glyph);
    }

    const delegated = snapshot.instructions.filter(
        (instruction): instruction is DelegatedInstruction => instruction.kind === 'delegated',
    );
    const delegatedByLine = snapshot.lineMetrics.map(() => [] as DelegatedInstruction[]);
    lineCursor = 0;
    for (const item of delegated) {
        lineCursor = advanceLineCursor(snapshot, { x: item.x, y: item.y }, lineCursor);
        delegatedByLine[lineCursor]?.push(item);
    }

    return {
        glyphByByteStart,
        glyphsByLine,
        delegatedByLine,
        delegatedBytes: new Set(delegated.map((item) => item.byteStart)),
        lineOffsets: snapshot.lineMetrics.map((line, index) => {
            let minimum = Infinity;
            for (const glyph of glyphsByLine[index] ?? []) minimum = Math.min(minimum, glyph.start);
            return line.byteStart - (Number.isFinite(minimum) ? minimum : 0);
        }),
        orientationByGlyphKey: buildOrientationByGlyphKey(snapshot.instructions),
        byLine: snapshot.lineMetrics.map(() => undefined),
    };
};

/**
 * 读取或首次创建 snapshot 的共享命中索引。
 *
 * @param snapshot - 当前不可变排版快照。
 * @returns 与 snapshot 身份绑定的索引。
 */
const getHitboxIndex = (snapshot: Snapshot): HitboxIndex => {
    const cached = hitboxCache.get(snapshot);
    if (cached) return cached;
    const index = createHitboxIndex(snapshot);
    hitboxCache.set(snapshot, index);
    return index;
};

/**
 * 只为指定行创建并缓存视觉顺序 hitboxes。
 *
 * 竖排先合并普通 glyph 与 delegated run，再统一还原字格；横排先按 X 排序，再使用下一个字形起点
 * 补齐普通 glyph 的 advance。结果写入 `index.byLine`，后续 caret、点击和拖选可直接复用同一数组。
 *
 * @param snapshot - 当前不可变排版快照。
 * @param index - 已完成 glyph/delegated 分桶的共享索引。
 * @param lineIndex - 需要实际创建 hitboxes 的行索引。
 * @returns 目标行按视觉行内起点排序的 hitboxes；行不存在时返回空数组。
 */
const buildLineHitboxes = (snapshot: Snapshot, index: HitboxIndex, lineIndex: number): HitBox[] => {
    const cached = index.byLine[lineIndex];
    if (cached) return cached;
    const line = snapshot.lineMetrics[lineIndex];
    if (!line) return [];
    const globalOffset = index.lineOffsets[lineIndex] ?? 0;
    const hitboxes: HitBox[] = [];

    if (snapshot.writingMode === 'vertical-rl') {
        const pending = (index.glyphsByLine[lineIndex] ?? [])
            .filter((glyph) => !index.delegatedBytes.has(glyph.start))
            .map((glyph) => ({
                originY: glyph.y,
                isUpright: index.orientationByGlyphKey.get(glyph.glyphKey) !== 'rotated90Cw',
                byteStart: globalOffset + glyph.start,
                byteEnd: globalOffset + glyph.end,
                clusterText: glyph.clusterText,
                bidiLevel: glyph.bidiLevel,
                knownAdvance: undefined as number | undefined,
            }));
        for (const item of index.delegatedByLine[lineIndex] ?? []) {
            pending.push({
                originY: item.y,
                isUpright: false,
                knownAdvance: item.advance,
                byteStart: globalOffset + item.byteStart,
                byteEnd: globalOffset + item.byteEnd,
                clusterText: item.text,
                bidiLevel: index.glyphByByteStart.get(item.byteStart)?.bidiLevel ?? 0,
            });
        }
        pending.sort((a, b) => a.originY - b.originY || a.byteStart - b.byteStart);
        const boxes = resolveVerticalInlineBoxes(line.top, line.bottom, snapshot.listMarkerIndent, pending);
        pending.forEach((item, itemIndex) => {
            const box = boxes[itemIndex];
            hitboxes.push({
                x: line.leftEdgeX,
                y: box?.start ?? item.originY + snapshot.listMarkerIndent,
                width: Math.max(line.rightEdgeX - line.leftEdgeX, 1),
                height: box?.size ?? 0,
                byteStart: item.byteStart,
                byteEnd: item.byteEnd,
                clusterText: item.clusterText,
                lineIndex,
                bidiLevel: item.bidiLevel,
                writingMode: snapshot.writingMode,
            });
        });
    } else {
        for (const glyph of index.glyphsByLine[lineIndex] ?? []) {
            if (index.delegatedBytes.has(glyph.start)) continue;
            hitboxes.push({
                x: glyph.x + snapshot.listMarkerIndent,
                y: line.top,
                width: 0,
                height: Math.max(line.bottom - line.top, 1),
                byteStart: globalOffset + glyph.start,
                byteEnd: globalOffset + glyph.end,
                clusterText: glyph.clusterText,
                lineIndex,
                bidiLevel: glyph.bidiLevel,
                writingMode: snapshot.writingMode,
            });
        }
        for (const item of index.delegatedByLine[lineIndex] ?? []) {
            hitboxes.push({
                x: item.x + snapshot.listMarkerIndent,
                y: line.top,
                width: item.advance,
                height: Math.max(line.bottom - line.top, 1),
                byteStart: globalOffset + item.byteStart,
                byteEnd: globalOffset + item.byteEnd,
                clusterText: item.text,
                lineIndex,
                bidiLevel: index.glyphByByteStart.get(item.byteStart)?.bidiLevel ?? 0,
                writingMode: snapshot.writingMode,
            });
        }
        hitboxes.sort((a, b) => getInlineStart(a) - getInlineStart(b));
        for (let itemIndex = 0; itemIndex < hitboxes.length; itemIndex += 1) {
            const hitbox = hitboxes[itemIndex];
            if (getInlineSize(hitbox) > 0) continue;
            const next = hitboxes[itemIndex + 1];
            const inlineEnd = next ? getInlineStart(next) : line.rightEdgeX + snapshot.listMarkerIndent;
            hitbox.width = Math.max(0, inlineEnd - getInlineStart(hitbox));
        }
    }

    const result = hitboxes.sort((a, b) => getInlineStart(a) - getInlineStart(b));
    index.byLine[lineIndex] = result;
    return result;
};

/**
 * 按需读取指定 snapshot 行的命中盒。
 *
 * 首次访问只创建目标行，其他行保持未物化状态；再次访问同一行返回缓存数组。该入口供折叠 caret、
 * 点击和方向键等单行交互使用，避免它们触发全文 hitbox 构建。
 *
 * @param snapshot - 当前排版快照；null 表示没有可用排版结果。
 * @param lineIndex - 目标行索引。
 * @returns 目标行命中盒；snapshot 或行无效时返回空数组。
 */
export const getHitboxesForSnapshotLine = (snapshot: Snapshot | null, lineIndex: number): HitBox[] => {
    if (!snapshot || !snapshot.lineMetrics[lineIndex]) return [];
    return buildLineHitboxes(snapshot, getHitboxIndex(snapshot), lineIndex);
};

/**
 * 根据排版快照建立并缓存全部行命中盒。
 *
 * 该入口保留给非折叠选区等确实需要跨行遍历的路径。它复用此前已经按需创建的行，只补齐剩余行，
 * 并缓存最终扁平数组；普通 caret 与点击应优先使用 `getHitboxesForSnapshotLine()`。
 *
 * @param snapshot - 当前排版快照；null 表示没有可用排版结果。
 * @returns 按排版行和行内视觉顺序排列的全部命中盒。
 */
export const buildHitboxes = (snapshot: Snapshot | null): HitBox[] => {
    if (!snapshot || snapshot.lineMetrics.length === 0) return [];
    const index = getHitboxIndex(snapshot);
    if (index.all) return index.all;
    index.all = snapshot.lineMetrics.flatMap((_line, lineIndex) => buildLineHitboxes(snapshot, index, lineIndex));
    return index.all;
};

/**
 * 获取指定行的命中盒。
 *
 * @param hitboxes - 全部命中盒。
 * @param lineIndex - 目标行索引。
 * @returns 按视觉起点排序的目标行命中盒。
 */
export const getHitboxesForLine = (hitboxes: readonly HitBox[], lineIndex: number) =>
    hitboxes.filter((hitbox) => hitbox.lineIndex === lineIndex).sort((a, b) => getInlineStart(a) - getInlineStart(b));

/**
 * 查找行内坐标之前最近的命中盒。
 *
 * @param hitboxes - 已按视觉起点排序的命中盒。
 * @param inline - 当前书写方向上的行内坐标。
 * @returns 命中的盒；坐标位于首个盒之前或数组为空时返回 null。
 */
export const findHitboxByInline = (hitboxes: readonly HitBox[], inline: number) => {
    if (hitboxes.length === 0 || inline < getInlineStart(hitboxes[0])) return null;
    let left = 0;
    let right = hitboxes.length - 1;
    while (left < right) {
        const middle = (left + right + 1) >>> 1;
        if (getInlineStart(hitboxes[middle]) <= inline) left = middle;
        else right = middle - 1;
    }
    return hitboxes[left];
};

/**
 * 读取命中盒在当前书写方向上的起点。
 *
 * @param hitbox - 目标命中盒。
 * @returns 横排时返回 x，竖排时返回 y。
 */
export const getHitboxInlineStart = getInlineStart;

/**
 * 读取命中盒在当前书写方向上的尺寸。
 *
 * @param hitbox - 目标命中盒。
 * @returns 横排时返回 width，竖排时返回 height。
 */
export const getHitboxInlineSize = getInlineSize;

/**
 * 将命中盒的视觉边缘转换为逻辑字节偏移。
 *
 * @param hitbox - 目标命中盒。
 * @param edge - 视觉起点或终点。
 * @returns 考虑双向文本方向后的 UTF-8 字节偏移。
 */
export const getVisualEdgeOffset = (hitbox: HitBox, edge: 'start' | 'end') => {
    const reverse = hitbox.bidiLevel % 2 === 1;
    if (edge === 'start') return reverse ? hitbox.byteEnd : hitbox.byteStart;
    return reverse ? hitbox.byteStart : hitbox.byteEnd;
};
